import * as Yup from "yup";
import ChevronDownIcon from "../../../../../../../assets/svg/chevron-down-icon";
import CheckboxCheckedIcon from "../../../../../../../assets/svg/checkbox-checked-icon";
import HashtagIcon from "../../../../../../../assets/svg/hashtag-icon";
import CalendarIcon from "../../../../../../../assets/svg/calendar-icon";
import CheckIcon from "../../../../../../../assets/svg/check-icon";
import ConciseIcon from "../../../../../../../assets/svg/concise-icon";
import TextFormattingIcon from "../../../../../../../assets/svg/text-formatting-icon";
import RadioButtonSelectedIcon from "../../../../../../../assets/svg/radio-button-selected-icon";
import type { CustomFieldType, FieldOption } from "./types";
import type { HelpdeskCustomFieldDefinition } from "../../TicketDetails/components/TicketOverview/utils/helpdeskCustomFields";
import {
  normalizeHelpdeskFieldType,
  HELPDESK_CUSTOM_FIELD_TYPES,
  getEffectiveDropdownOptions,
} from "../../TicketDetails/components/TicketOverview/utils/helpdeskCustomFields";
import { getHelpdeskLabels, getRegionLabel } from "../../../../../../../utils/constants/mappings";

export const UI_TO_API_FIELD_TYPE: Record<CustomFieldType, string> = {
  dropdown: "dropdown_single_select",
  "dropdown-multi": "dropdown_multi_select",
  text: "text",
  "long-text": "long_text",
  radio: "radio_single_select",
  checkbox: "checkbox",
  number: "number",
  date: "date",
};

export const FEATURE_REQUESTS_SYSTEM_NAME = "Feature Requests";

/**
 * File types accepted by ticket attachment pickers (native input `accept`).
 * Images + PDF/DOC/DOCX/CSV + Excel (.xls/.xlsx).
 */
export const TICKET_ATTACHMENT_ACCEPT =
  "image/*,.pdf,.doc,.docx,.csv,.xls,.xlsx";

/** Human-readable list of supported attachment formats, shown in the upload dropzone. */
export const TICKET_ATTACHMENT_FORMATS_LABEL =
  "PDF, PNG, JPG, DOC, CSV, XLS, XLSX up to 10MB";

export const PRIORITY_NUMBER_TO_STRING: Record<number, string> = {
  2: "high",
  1: "medium",
  0: "low",
};

export const PRIORITY_STRING_TO_NUMBER: Record<string, number> = {
  high: 2,
  medium: 1,
  low: 0,
};

export const TICKET_DESCRIPTION_TOOLBAR_SCHEMA = {
  history: true,
  typography: {
    fontFamily: true,
    fontSize: true,
  },
  textFormatting: {
    bold: true,
    italic: true,
    underline: true,
    strikethrough: true,
  },
  textStyling: {
    textColor: true,
  },
  contentStructure: {
    lists: true,
    alignment: true,
    indentation: true,
    blockquotes: false,
  },
  media: {
    links: false,
    images: true,
    attachments: false,
  },
  interactive: {
    emojis: false,
  },
  utilities: {
    clearFormatting: false,
  },
  mentions: {
    mentions: false,
  },
};

export const FIELD_OPTIONS: FieldOption[] = [
  {
    id: "dropdown",
    label: "Dropdown",
    icon: ChevronDownIcon,
    iconClass: "w-[10px] h-[10px] text-verba-indigo dark:text-verba-indigo-dk",
  },
  {
    id: "dropdown-multi",
    label: "Dropdown Multi-Select",
    icon: CheckIcon,
    iconClass: "w-[18px] h-[18px] text-verba-blue dark:text-verba-blue-dk",
  },
  {
    id: "text",
    label: "Text",
    icon: ConciseIcon,
    iconClass: "w-[18px] h-[18px] text-verba-indigo dark:text-verba-indigo-dk",
  },
  {
    id: "long-text",
    label: "Long Text Area",
    icon: TextFormattingIcon,
    iconClass: "w-[18px] h-[18px] text-verba-indigo dark:text-verba-indigo-dk",
  },
  {
    id: "radio",
    label: "Radio (single select)",
    icon: RadioButtonSelectedIcon,
    iconClass: "w-[18px] h-[18px] text-verba-blue dark:text-verba-blue-dk",
  },
  {
    id: "checkbox",
    label: "Checkbox",
    icon: CheckboxCheckedIcon,
    iconClass: "w-[18px] h-[18px] text-verba-blue dark:text-verba-blue-dk",
  },
  {
    id: "number",
    label: "Number",
    icon: HashtagIcon,
    iconClass:
      "w-[18px] h-[18px] text-analytics-safety dark:text-analytics-safety-dk",
  },
  {
    id: "date",
    label: "Date",
    icon: CalendarIcon,
    iconClass: "w-[18px] h-[18px] text-verba-indigo dark:text-verba-indigo-dk",
  },
];

export const DEFAULT_CATEGORIES = [
  { id: "Helpdesk Support", label: "Helpdesk Support" },
  { id: "Sales & Marketing", label: "Sales & Marketing" },
];

export type TicketCategoryItem = (typeof DEFAULT_CATEGORIES)[number];

export const NOWSTUDENTS_CATEGORY_ACCESS_KEY_MAP: Record<
  string,
  TicketCategoryItem
> = {
  helpdesk_support: { id: "Helpdesk Support", label: "Helpdesk Support" },
  sales_and_marketing: {
    id: "Sales & Marketing",
    label: "Sales & Marketing",
  },
};

export const PRIORITIES = [
  {
    label: "High",
    value: 2,
    sla: "2 working days",
    flagClass: "text-verba-red-alert dark:text-verba-red-alert-dk",
    selectedBorderClass:
      "border-verba-red-alert dark:border-verba-red-alert-dk bg-user-management-red-card-bg dark:bg-user-management-red-card-bg-dk",
  },
  {
    label: "Moderate",
    value: 1,
    sla: "5 working days",
    flagClass:
      "text-dark-yellow-for-alerts dark:text-dark-yellow-for-alerts-dk",
    selectedBorderClass:
      "border-dark-yellow-for-alerts dark:border-dark-yellow-for-alerts-dk bg-dark-yellow-for-alerts/10 dark:bg-dark-yellow-for-alerts-dk/10",
  },
  {
    label: "Low",
    value: 0,
    sla: "10 working days",
    flagClass: "text-verba-blue dark:text-verba-blue-dk",
    selectedBorderClass:
      "border-verba-blue dark:border-verba-blue-dk bg-applied-filters-tag-bg dark:bg-applied-filters-tag-bg-dk",
  },
];

export const SITE_NAME_OPTIONS = [
  {
    value: "belgrave-view-birmingham",
    content: (
      <span className="text-md text-heading-text dark:text-heading-text-dk">
        Belgrave View - Birmingham
      </span>
    ),
  },
  {
    value: "city-centre-manchester",
    content: (
      <span className="text-md text-heading-text dark:text-heading-text-dk">
        City Centre - Manchester
      </span>
    ),
  },
  {
    value: "riverside-london",
    content: (
      <span className="text-md text-heading-text dark:text-heading-text-dk">
        Riverside - London
      </span>
    ),
  },
];

export const SYSTEM_NAME_OPTIONS = [
  {
    value: "concurrent",
    content: (
      <span className="text-md text-heading-text dark:text-heading-text-dk flex items-center gap-1.5">
        <span className="text-verba-blue dark:text-verba-blue-dk">⚡</span>
        Concurrent
      </span>
    ),
    selectedOptionContent: (
      <span className="text-md text-heading-text dark:text-heading-text-dk flex items-center gap-1.5">
        <span className="text-verba-blue dark:text-verba-blue-dk">⚡</span>
        Concurrent
      </span>
    ),
  },
  {
    value: "sequential",
    content: (
      <span className="text-md text-heading-text dark:text-heading-text-dk flex items-center gap-1.5">
        <span className="text-verba-blue dark:text-verba-blue-dk">⚡</span>
        Sequential
      </span>
    ),
    selectedOptionContent: (
      <span className="text-md text-heading-text dark:text-heading-text-dk flex items-center gap-1.5">
        <span className="text-verba-blue dark:text-verba-blue-dk">⚡</span>
        Sequential
      </span>
    ),
  },
];

export const ISSUE_TYPE_OPTIONS = [
  {
    value: "appointments",
    content: (
      <span className="text-md text-heading-text dark:text-heading-text-dk">
        Appointments
      </span>
    ),
  },
  {
    value: "billing",
    content: (
      <span className="text-md text-heading-text dark:text-heading-text-dk">
        Billing
      </span>
    ),
  },
  {
    value: "technical",
    content: (
      <span className="text-md text-heading-text dark:text-heading-text-dk">
        Technical
      </span>
    ),
  },
  {
    value: "general",
    content: (
      <span className="text-md text-heading-text dark:text-heading-text-dk">
        General
      </span>
    ),
  },
];

export const getTagOptions = (clientCode?: string) => {
  const viewingRequestedLabel = getRegionLabel(
    "viewingRequested",
    clientCode
  );
  return [
    {
      value: "viewing-requested",
      label: viewingRequestedLabel,
      content: (
        <span className="text-md text-heading-text dark:text-heading-text-dk">
          {viewingRequestedLabel}
        </span>
      ),
    },
    {
      value: "bug-report",
      label: "Bug report",
      content: (
        <span className="text-md text-heading-text dark:text-heading-text-dk">
          Bug report
        </span>
      ),
    },
    {
      value: "requested-support",
      label: "Requested Support",
      content: (
        <span className="text-md text-heading-text dark:text-heading-text-dk">
          Requested Support
        </span>
      ),
    },
    {
      value: "high-priority",
      label: "High Priority",
      content: (
        <span className="text-md text-heading-text dark:text-heading-text-dk">
          High Priority
        </span>
      ),
    },
    {
      value: "feature-request",
      label: "Feature Request",
      content: (
        <span className="text-md text-heading-text dark:text-heading-text-dk">
          Feature Request
        </span>
      ),
    },
    {
      value: "documentation",
      label: "Documentation",
      content: (
        <span className="text-md text-heading-text dark:text-heading-text-dk">
          Documentation
        </span>
      ),
    },
    {
      value: "enhancement",
      label: "Enhancement",
      content: (
        <span className="text-md text-heading-text dark:text-heading-text-dk">
          Enhancement
        </span>
      ),
    },
    {
      value: "urgent",
      label: "Urgent",
      content: (
        <span className="text-md text-heading-text dark:text-heading-text-dk">
          Urgent
        </span>
      ),
    },
  ];
};

/** Issue type is only mandatory when the selected system actually has options to pick from. */
const hasIssueTypeOptions = (
  issueTypeField: HelpdeskCustomFieldDefinition | null | undefined,
  systemName: string | undefined
) => {
  if (!issueTypeField || !systemName) return false;
  return (
    getEffectiveDropdownOptions(issueTypeField, {
      "system-name": systemName,
    })?.length > 0
  );
};

const getBaseSchema = (
  region?: string,
  issueTypeField?: HelpdeskCustomFieldDefinition | null
) => {
  const helpdeskLabels = getHelpdeskLabels(region);
  return {
    ticketCategory: Yup.string().required(helpdeskLabels.ticketCategoryRequired),
    fullName: Yup.string()
      .required("Full name is required")
      .min(2, "Name must be at least 2 characters"),
    email: Yup.string()
      .email("Invalid email address")
      .required("Email is required"),
    siteName: Yup.string().required("Site name is required"),
    ticketSubject: Yup.string()
      .required(helpdeskLabels.ticketSubjectRequired)
      .min(3, "Subject must be at least 3 characters"),
    systemName: Yup.string().required("System name is required"),
    issueType: Yup.string().when("systemName", {
      is: (systemName: string) =>
        systemName !== FEATURE_REQUESTS_SYSTEM_NAME &&
        hasIssueTypeOptions(issueTypeField, systemName),
      then: (schema) => schema.required("Issue type is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    priority: Yup.number().when("systemName", {
      is: FEATURE_REQUESTS_SYSTEM_NAME,
      then: (schema) => schema.notRequired().nullable(),
      otherwise: (schema) => schema.required("Priority is required"),
    }),
    ticketDescription: Yup.string().required(
      `${helpdeskLabels.ticketDescription} is required`
    ),
  };
};

export const getTicketValidationSchema = (region?: string) =>
  Yup.object(getBaseSchema(region));

export const ticketValidationSchema = Yup.object(getBaseSchema());

export function buildTicketValidationSchema(
  customFields: HelpdeskCustomFieldDefinition[],
  region?: string,
  issueTypeField?: HelpdeskCustomFieldDefinition | null
) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const shape: Record<string, any> = {};

  for (const field of customFields) {
    if (!field?.is_required || !field?.is_active) continue;
    const t = normalizeHelpdeskFieldType(field?.type);

    switch (t) {
      case HELPDESK_CUSTOM_FIELD_TYPES.DROPDOWN_MULTI:
        shape[field?.key] = Yup.array()
          .required(`${field?.name} is required`)
          .min(1, `${field?.name} is required`);
        break;
      case HELPDESK_CUSTOM_FIELD_TYPES.CHECKBOX:
        if (field?.values_for_dropdown?.length) {
          shape[field?.key] = Yup.mixed().test(
            "checkbox-options-required",
            `${field?.name} is required`,
            (val) => Array.isArray(val) && val?.length > 0
          );
        } else {
          shape[field?.key] = Yup.mixed().test(
            "checkbox-boolean-required",
            `${field?.name} is required`,
            (val) => val === true
          );
        }
        break;
      case HELPDESK_CUSTOM_FIELD_TYPES.NUMBER:
        shape[field?.key] = Yup.mixed().test(
          "is-number",
          `${field?.name} is required`,
          (val) => val !== "" && val !== null && val !== undefined
        );
        break;
      case HELPDESK_CUSTOM_FIELD_TYPES.DATE:
        shape[field?.key] = Yup.string()
          .nullable()
          .required(`${field?.name} is required`);
        break;
      default:
        shape[field?.key] = Yup.string().required(`${field?.name} is required`);
    }
  }

  return Yup.object({
    ...getBaseSchema(region, issueTypeField),
    custom_fields:
      Object.keys(shape).length > 0 ? Yup.object().shape(shape) : Yup.object(),
  });
}
